/** Mirrors health-hrms-backend visit-frequency date expansion. */

const MS_DAY = 24 * 60 * 60 * 1000;

export const parseDateOnly = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(
      Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()),
    );
  }
  const raw = String(value || "").slice(0, 10);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};

export const formatDateOnly = (value) => {
  const d = value instanceof Date ? value : parseDateOnly(value);
  if (!d) return "";
  return d.toISOString().slice(0, 10);
};

export const addUtcDays = (value, days) =>
  new Date(value.getTime() + days * MS_DAY);

export const durationEndDate = (startDate, durationWeeks) => {
  const start = parseDateOnly(startDate);
  if (!start) return "";
  const weeks = Math.max(1, Number(durationWeeks) || 1);
  return formatDateOnly(addUtcDays(start, weeks * 7 - 1));
};

export const defaultVisitDays = (startDate, timesPerWeek) => {
  const start = parseDateOnly(startDate);
  if (!start) return [];
  const n = Math.max(1, Math.min(7, Number(timesPerWeek) || 1));
  const startDow = start.getUTCDay();
  return Array.from({ length: n }, (_, i) => (startDow + i) % 7);
};

export const uniqueVisitDays = (visitDays = []) => {
  const unique = [
    ...new Set(
      (visitDays || []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6),
    ),
  ];
  unique.sort((a, b) => a - b);
  return unique;
};

export const visitDaysMatchFrequency = (visitDays, timesPerWeek) => {
  const times = Math.max(1, Math.min(7, Number(timesPerWeek) || 1));
  return uniqueVisitDays(visitDays).length === times;
};

export const expandFrequencyVisitDates = ({
  startDate,
  endDate,
  timesPerWeek,
  durationWeeks,
  useDefaultDays,
  visitDays,
}) => {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!start || !end || end.getTime() < start.getTime()) return [];
  const times = Math.max(1, Math.min(7, Number(timesPerWeek) || 1));
  const weeks = Math.max(1, Math.min(26, Number(durationWeeks) || 1));
  const target = times * weeks;
  let days = [];
  if (useDefaultDays) {
    days = defaultVisitDays(start, times);
  } else {
    days = [...new Set((visitDays || []).filter((d) => d >= 0 && d <= 6))];
    days.sort((a, b) => a - b);
    if (!days.length) days = defaultVisitDays(start, times);
    if (days.length > times) days = days.slice(0, times);
  }

  const dates = [];
  const startDow = start.getUTCDay();
  let weekStart = addUtcDays(start, -startDow);
  for (let w = 0; w < weeks + 2 && dates.length < target; w += 1) {
    const weekDays = [...days].sort((a, b) => {
      const da = (a - startDow + 7) % 7;
      const db = (b - startDow + 7) % 7;
      return da - db;
    });
    for (const dow of weekDays) {
      if (dates.length >= target) break;
      const date = addUtcDays(weekStart, dow);
      if (date.getTime() < start.getTime()) continue;
      if (date.getTime() > end.getTime()) continue;
      dates.push(formatDateOnly(date));
    }
    weekStart = addUtcDays(weekStart, 7);
    if (weekStart.getTime() > end.getTime()) break;
  }
  return dates;
};

export const daysLeftInEpisode = (episodeEnd, fromDate) => {
  const end = parseDateOnly(episodeEnd);
  const from = parseDateOnly(fromDate) || parseDateOnly(new Date());
  if (!end || !from) return 0;
  const diff = Math.round((end.getTime() - from.getTime()) / MS_DAY);
  return Math.max(0, diff);
};

export const frequencyDateRangesOverlap = (aStart, aEnd, bStart, bEnd) => {
  const as = parseDateOnly(aStart);
  const ae = parseDateOnly(aEnd);
  const bs = parseDateOnly(bStart);
  const be = parseDateOnly(bEnd);
  if (!as || !ae || !bs || !be) return false;
  return as.getTime() <= be.getTime() && bs.getTime() <= ae.getTime();
};

/** Same episode + discipline + employee with overlapping dates. */
export const findDuplicateStaffFrequency = (plans, candidate) => {
  const staffId = Number(candidate?.staffId);
  const discipline = String(candidate?.discipline || "").toUpperCase();
  const episodeId = Number(candidate?.episodeId);
  const excludeId = candidate?.excludeId != null ? Number(candidate.excludeId) : null;
  if (!staffId || !discipline || !candidate?.startDate || !candidate?.endDate) {
    return null;
  }
  return (
    (plans || []).find((plan) => {
      if (excludeId != null && Number(plan.id) === excludeId) return false;
      if (episodeId && Number(plan.episodeId) !== episodeId) return false;
      if (String(plan.discipline || "").toUpperCase() !== discipline) return false;
      if (Number(plan.staffId) !== staffId) return false;
      return frequencyDateRangesOverlap(
        plan.startDate,
        plan.endDate,
        candidate.startDate,
        candidate.endDate,
      );
    }) || null
  );
};

/** Scheduled visit with no View Frequency plan — yellow ring (empty or filled). */
export const isFrequencyMissingVisit = (visit) => {
  if (!visit) return false;
  if (visit.frequencyMissing === true) return true;
  if (visit.frequencyMissing === false) return false;
  const fromFreq =
    visit.frequencyPlanId != null ||
    String(visit.source || "").toLowerCase() === "frequency";
  return !fromFreq;
};
